import React from 'react';

export type PromoPlanKind = 'core' | 'scan_plus';

const RH_REFERRAL_DISCOUNTS: Record<PromoPlanKind, number> = {
  core: 50,
  scan_plus: 100,
};

/** Display amounts when Stripe products are not loaded yet. */
export const PLAN_FALLBACK_AMOUNTS: Record<string, number> = {
  coreVitals: 38,
  coreVitalsPlus: 175,
  fullBody: 1650,
};

export function getPlanFallbackAmount(planId: string): number | null {
  return PLAN_FALLBACK_AMOUNTS[planId] ?? null;
}

export function planIdToPromoKind(planId: string): PromoPlanKind | null {
  if (planId === 'coreVitals') return 'core';
  if (planId === 'coreVitalsPlus' || planId === 'fullBody') return 'scan_plus';
  return null;
}

export function isRhReferralPricingPath(pathname: string = window.location.pathname): boolean {
  const path = pathname.replace(/\/$/, '') || '/';
  if (path === '/plans') return true;
  if (path === '/plans-core' || path.startsWith('/plans-core/')) return true;
  if (path === '/full-body-scan' || path.startsWith('/full-body-scan/')) return true;
  if (path === '/signup/today' || path.startsWith('/signup/')) return true;
  return false;
}

export function hasRhReferrer(): boolean {
  const rhForm = (window as any)?.RH_MF70bd716e94;
  return Boolean(rhForm?.referrer);
}

/** Referral discount applies when referrer is set on allowed checkout URLs only. */
export function shouldApplyRhReferralDiscount(
  pathname: string = window.location.pathname
): boolean {
  return hasRhReferrer() && isRhReferralPricingPath(pathname);
}

export function getRhReferralDiscountAmount(kind: PromoPlanKind): number {
  return RH_REFERRAL_DISCOUNTS[kind];
}

export function applyRhReferralDiscount(
  amount: number,
  kind: PromoPlanKind,
  pathname?: string
): number {
  if (!shouldApplyRhReferralDiscount(pathname)) return amount;
  return Math.max(0, amount - getRhReferralDiscountAmount(kind));
}

export function formatGBP(amount: number): string {
  const rounded = Math.round((amount + Number.EPSILON) * 100) / 100;
  const hasDecimals = Math.abs(rounded % 1) > Number.EPSILON;
  return `£${hasDecimals ? rounded.toFixed(2) : Math.round(rounded).toLocaleString()}`;
}

type RenderRhReferralPriceOpts = {
  amount: number;
  kind: PromoPlanKind;
  prefix?: string;
  suffix?: React.ReactNode;
  billingPeriod?: 'month' | 'year' | 'once-off';
  pathname?: string;
};

export function renderRhReferralPrice(opts: RenderRhReferralPriceOpts): React.ReactNode {
  const { amount, kind, prefix, suffix, billingPeriod = 'month', pathname } = opts;

  if (!shouldApplyRhReferralDiscount(pathname)) {
    return (
      <>
        {prefix}
        {formatGBP(amount)}
        {suffix}
      </>
    );
  }

  const discounted = applyRhReferralDiscount(amount, kind, pathname);
  const todayLabel =
    discounted <= 0
      ? `${formatGBP(0)} today (referral discount)`
      : `${formatGBP(discounted)} today (referral discount)`;

  const showRenewNote =
    billingPeriod === 'month' && amount > 0;

  return (
    <div className='rh-referral-price-wrap'>
      <div className='rh-referral-price-main'>
        <span className='rh-referral-price-original'>{formatGBP(amount)}</span>
        <span className='rh-referral-price-discounted'>
          {discounted <= 0 ? formatGBP(0) : formatGBP(discounted)}
        </span>
        {suffix}
      </div>
      <div className='rh-referral-footnotes'>
        <div className='rh-referral-today'>{todayLabel}</div>
        {showRenewNote && (
          <div className='rh-referral-renew'>
            Renews at {formatGBP(amount)}/{billingPeriod}
          </div>
        )}
      </div>
    </div>
  );
}

type SelectedPlanForPricing = {
  type: string;
  paymentType?: 'monthly' | 'yearly';
  product?: {
    monthly_amount: number | null;
    yearly_amount: number | null;
  } | null;
};

/** Order-summary price for signup (/signup/today) after plan selection. */
export function renderSelectedPlanPrice(selectedPlan: SelectedPlanForPricing): React.ReactNode {
  const paymentType = selectedPlan.paymentType || 'monthly';

  if (selectedPlan.type === 'fullBody') {
    const amount = selectedPlan.product?.yearly_amount ?? 1650;
    return renderRhReferralPrice({
      amount,
      kind: 'scan_plus',
      billingPeriod: 'once-off',
      suffix: <span>/once-off</span>,
    });
  }

  const kind = planIdToPromoKind(selectedPlan.type);
  if (!kind) {
    return <>£40<span>/month</span></>;
  }

  const amountFromProduct = selectedPlan.product
    ? paymentType === 'monthly'
      ? selectedPlan.product.monthly_amount
      : selectedPlan.product.yearly_amount
    : null;

  const amount =
    amountFromProduct ?? getPlanFallbackAmount(selectedPlan.type) ?? null;

  if (amount == null) {
    return <>£40<span>/month</span></>;
  }

  const billingPeriod = paymentType === 'monthly' ? 'month' : 'year';
  return renderRhReferralPrice({
    amount,
    kind,
    suffix: <span>/{billingPeriod}</span>,
    billingPeriod,
  });
}

type CheckoutPlanPriceOpts = {
  planId: string;
  amount: number;
  paymentType: 'monthly' | 'yearly';
  showTilde?: boolean;
  onceOff?: boolean;
};

/** Plan card / order-summary price on checkout plan pages. */
export function renderCheckoutPlanPrice(opts: CheckoutPlanPriceOpts): React.ReactNode {
  const { planId, amount, paymentType, showTilde, onceOff } = opts;
  const kind = planIdToPromoKind(planId) ?? 'scan_plus';

  if (onceOff || planId === 'fullBody') {
    return renderRhReferralPrice({
      amount,
      kind: 'scan_plus',
      billingPeriod: 'once-off',
      suffix: onceOff ? <span>/once-off</span> : undefined,
    });
  }

  const billingPeriod = paymentType === 'monthly' ? 'month' : 'year';
  return renderRhReferralPrice({
    amount,
    kind,
    prefix: showTilde ? '~ ' : '',
    suffix: <span>/{billingPeriod}</span>,
    billingPeriod,
  });
}
